// Products API - Functions for loading mattress configuration from Supabase
const { supabase } = require('../supabase.js')

/**
 * Products API for mattress configurator
 */
const productsApi = {
  /**
   * Get configuration in format compatible with existing frontend
   * @returns {Promise<FrontendConfig>}
   */
  async getConfig() {
    try {
      console.log('🔄 Loading configuration from Supabase...')
      
      // Fetch layers with prices
      const { data: layers, error: layersError } = await supabase
        .from('mattress_layers')
        .select(`
          id,
          name,
          slug,
          description,
          icon,
          available_heights,
          layer_prices (
            size,
            price
          )
        `)
        .order('name')

      if (layersError) {
        console.error('❌ Error loading layers:', layersError)
        throw new Error(`Failed to load layers: ${layersError.message}`)
      }

      // Fetch covers
      const { data: covers, error: coversError } = await supabase
        .from('covers')
        .select('*')
        .order('name')

      if (coversError) {
        console.error('❌ Error loading covers:', coversError)
        throw new Error(`Failed to load covers: ${coversError.message}`)
      }

      // Transform layers to frontend format
      const mattressLayers = layers?.map(layer => {
        // Convert layer_prices array to prices object
        const prices = layer.layer_prices.reduce((acc, price) => {
          acc[price.size] = price.price
          return acc
        }, {})

        return {
          id: layer.slug, // Используем slug как ID для совместимости с URL
          uuid: layer.id,  // Сохраняем UUID отдельно
          name: layer.name,
          slug: layer.slug,
          description: layer.description,
          icon: layer.icon,
          availableHeights: layer.available_heights,
          prices: prices,
          // For backward compatibility, add base price (first available size)
          price: layer.layer_prices[0]?.price || 0
        }
      }) || []

      // Transform covers to frontend format
      const frontendCovers = covers?.map(cover => ({
        id: cover.slug || cover.id, // Используем slug как ID для совместимости с URL
        uuid: cover.id,  // Сохраняем UUID отдельно
        name: cover.name,
        slug: cover.slug,
        price: parseFloat(cover.price),
        icon: cover.icon
      })) || []

      console.log(`✅ Loaded ${mattressLayers.length} layers and ${frontendCovers.length} covers`)

      return {
        mattressLayers,
        covers: frontendCovers
      }
    } catch (error) {
      console.error('💥 Failed to load configuration:', error)
      throw error
    }
  },

  /**
   * Get layers available for specific height
   * @param {number} height - Mattress height (10, 20, or 30)
   * @returns {Promise<FrontendLayer[]>}
   */
  async getLayersForHeight(height) {
    try {
      const { data, error } = await supabase
        .from('mattress_layers')
        .select(`
          id,
          name,
          slug,
          icon,
          available_heights,
          layer_prices (
            size,
            price
          )
        `)
        .contains('available_heights', [height])
        .order('name')

      if (error) {
        console.error(`❌ Error loading layers for height ${height}:`, error)
        throw new Error(`Failed to load layers for height ${height}: ${error.message}`)
      }

      // Transform to frontend format
      const layers = data?.map(layer => {
        const prices = layer.layer_prices.reduce((acc, price) => {
          acc[price.size] = price.price
          return acc
        }, {})

        return {
          id: layer.slug, // Используем slug как ID для совместимости с URL
          uuid: layer.id,  // Сохраняем UUID отдельно
          name: layer.name,
          slug: layer.slug,
          icon: layer.icon,
          availableHeights: layer.available_heights,
          prices: prices,
          price: layer.layer_prices[0]?.price || 0
        }
      }) || []

      console.log(`✅ Loaded ${layers.length} layers for height ${height}cm`)
      return layers
    } catch (error) {
      console.error(`💥 Failed to load layers for height ${height}:`, error)
      throw error
    }
  },

  /**
   * Get layer by slug
   * @param {string} slug - Layer slug
   * @returns {Promise<FrontendLayer|null>}
   */
  async getLayerBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('mattress_layers')
        .select(`
          id,
          name,
          slug,
          description,
          icon,
          available_heights,
          layer_prices (
            size,
            price
          )
        `)
        .eq('slug', slug)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        console.error(`❌ Error loading layer ${slug}:`, error)
        throw new Error(`Failed to load layer ${slug}: ${error.message}`)
      }

      if (!data) return null

      // Transform to frontend format
      const prices = data.layer_prices.reduce((acc, price) => {
        acc[price.size] = price.price
        return acc
      }, {})

      return {
        id: data.slug, // Используем slug как ID для совместимости с URL
        uuid: data.id,  // Сохраняем UUID отдельно
        name: data.name,
        slug: data.slug,
        description: data.description,
        icon: data.icon,
        availableHeights: data.available_heights,
        prices: prices,
        price: data.layer_prices[0]?.price || 0
      }
    } catch (error) {
      console.error(`💥 Failed to load layer ${slug}:`, error)
      throw error
    }
  },

  /**
   * Get cover by slug or ID
   * @param {string} identifier - Cover slug or ID
   * @returns {Promise<FrontendCover|null>}
   */
  async getCover(identifier) {
    try {
      const { data, error } = await supabase
        .from('covers')
        .select('*')
        .or(`slug.eq.${identifier},id.eq.${identifier}`)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error(`❌ Error loading cover ${identifier}:`, error)
        throw new Error(`Failed to load cover ${identifier}: ${error.message}`)
      }

      if (!data) return null

      return {
        id: data.slug || data.id, // Используем slug как ID для совместимости с URL
        uuid: data.id,  // Сохраняем UUID отдельно
        name: data.name,
        slug: data.slug,
        price: parseFloat(data.price),
        icon: data.icon
      }
    } catch (error) {
      console.error(`💥 Failed to load cover ${identifier}:`, error)
      throw error
    }
  },

  /**
   * Calculate total price for mattress configuration
   * @param {Object} config - Mattress configuration
   * @param {string} config.size - Mattress size (e.g., '160x200')
   * @param {number} config.height - Mattress height (10, 20, 30)
   * @param {string[]} config.layers - Array of layer slugs
   * @param {string} config.cover - Cover slug or ID
   * @returns {Promise<number>} Total price
   */
  async calculatePrice(config) {
    try {
      let totalPrice = 0

      // Calculate layer prices
      for (const layerSlug of config.layers) {
        if (!layerSlug) continue
        
        const layer = await this.getLayerBySlug(layerSlug)
        if (layer && layer.prices[config.size]) {
          totalPrice += layer.prices[config.size]
        }
      }

      // Add cover price
      if (config.cover) {
        const cover = await this.getCover(config.cover)
        if (cover) {
          totalPrice += cover.price
        }
      }

      return totalPrice
    } catch (error) {
      console.error('💥 Failed to calculate price:', error)
      throw error
    }
  }
}

module.exports = { productsApi };